/**
 * Animated theme switch, driven by the View Transitions API.
 *
 * `document.startViewTransition` takes a native compositor snapshot of the old
 * theme, applies the new one to the live DOM, then stacks the two as
 * `::view-transition-old(root)` (below) and `::view-transition-new(root)`
 * (above). We disable the browser's default crossfade in globals.css and
 * instead drive `clip-path` on the new layer every frame, so the incoming theme
 * is revealed through an animated mask. At progress 1 the pseudo layers are
 * dropped and the live DOM — already in the new theme — is all that is left.
 *
 * Nothing here moves layout: only the mask changes.
 *
 * Pseudo-elements cannot be styled inline, so the mask is written through a
 * mutated CSSStyleDeclaration on a rule we insert once.
 *
 * Wired only into the Settings toggle. Every other theme change — hydration,
 * the system-preference listener, a stray setTheme — stays instant.
 */

const DURATION_MS = 1000;
const TAU = Math.PI * 2;

export const clamp01 = (x: number) => Math.min(Math.max(x, 0), 1);

/** easeInOutCubic */
export const ease = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

/** Deterministic pseudo-random in [0,1) so effects look the same every run. */
export const hash = (i: number) => {
  const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
};

interface Point {
  x: number;
  y: number;
}

/** Returns the `clip-path` value for eased progress `p` and elapsed seconds `t`. */
type FrameFn = (p: number, t: number) => string;

/**
 * An effect is a factory: it measures once, then returns a per-frame function
 * closing over that measurement. Keeping the state inside the closure means
 * each effect can hold whatever shape it likes without a shared state union.
 */
type Effect = (width: number, height: number, origin: Point) => FrameFn;

/** An empty path — hides the new layer entirely. */
const EMPTY_CLIP = 'path("M0 0h0v0z")';

/** Distance from `c` to the furthest corner of a `w`×`h` box. */
export const coverRadius = (w: number, h: number, c: Point) =>
  Math.hypot(Math.max(c.x, w - c.x), Math.max(c.y, h - c.y));

export const EFFECTS: Record<string, Effect> = {
  "Radial wipe": (w, h, c) => {
    const R = coverRadius(w, h, c);
    return (p) => `circle(${(p * R).toFixed(1)}px at ${c.x}px ${c.y}px)`;
  },

  "Noise dissolve": (w, h) => {
    const size = 22;
    const cols = Math.ceil(w / size);
    const rows = Math.ceil(h / size);
    const cells: { x: number; y: number; key: number }[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        cells.push({ x: c * size, y: r * size, key: hash(r * cols + c) });
      }
    }
    cells.sort((a, b) => a.key - b.key);
    const strs = cells.map(
      (cl) => `M${cl.x} ${cl.y}h${size}v${size}h-${size}z`,
    );
    return (p) => {
      const k = Math.round(p * strs.length);
      return k === 0 ? EMPTY_CLIP : `path("${strs.slice(0, k).join("")}")`;
    };
  },

  "Blob sweep": (w, h) => {
    const D = w + h;
    return (p, t) => {
      const A = 0.1 * D;
      const E = -A - 40 + p * (D + 2 * A + 80);
      const N = 40;
      let d = "";
      for (let i = 0; i <= N; i++) {
        const y = -0.3 * h + (1.6 * h * i) / N;
        const wob =
          Math.sin(y * 0.008 + t * 1.3) * 0.55 +
          Math.sin(y * 0.021 - t * 0.9 + 2.0) * 0.3 +
          Math.sin(y * 0.043 + t * 2.1 + 4.0) * 0.15;
        const x = E - y + wob * A;
        d += `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
      }
      d += `L${(-2 * D).toFixed(0)} ${(1.3 * h).toFixed(0)}`;
      d += `L${(-2 * D).toFixed(0)} ${(-0.3 * h).toFixed(0)}z`;
      return `path("${d}")`;
    };
  },

  /** A radial wipe whose front is an amoeba rather than a circle. */
  "Blob burst": (w, h, c) => {
    const R = (coverRadius(w, h, c) + 40) / 0.62;
    return (p, t) => {
      const N = 72;
      let d = "";
      for (let i = 0; i <= N; i++) {
        const a = (i / N) * TAU;
        const wob =
          1 +
          0.2 * Math.sin(a * 3 + t * 1.6 + 1.0) +
          0.11 * Math.sin(a * 5 - t * 1.1 + 3.0) +
          0.07 * Math.sin(a * 8 + t * 2.3);
        const r = Math.max(p * R * wob, 0);
        d += `${i ? "L" : "M"}${(c.x + Math.cos(a) * r).toFixed(1)} ${(
          c.y + Math.sin(a) * r
        ).toFixed(1)}`;
      }
      return `path("${d}z")`;
    };
  },

  /** Blobs grow from scattered seeds and merge — subpaths union via nonzero fill. */
  "Metaball spread": (w, h, c) => {
    const seeds = [{ x: c.x, y: c.y, delay: 0 }];
    for (let j = 1; j < 6; j++) {
      seeds.push({
        x: hash(j * 3 + 1) * w,
        y: hash(j * 3 + 2) * h,
        delay: 0.12 + hash(j * 3) * 0.45,
      });
    }
    const shaped = seeds.map((sd) => ({
      ...sd,
      R: (coverRadius(w, h, sd) + 30) / 0.72,
      ph: hash(sd.x + sd.y) * TAU,
    }));
    return (p, t) => {
      const N = 44;
      let d = "";
      for (const sd of shaped) {
        const pj = clamp01((p - sd.delay) / (1 - sd.delay));
        if (pj <= 0) continue;
        for (let i = 0; i <= N; i++) {
          const a = (i / N) * TAU;
          const wob =
            1 +
            0.16 * Math.sin(a * 3 + sd.ph + t * 1.4) +
            0.12 * Math.sin(a * 6 - t + sd.ph * 2);
          const r = pj * sd.R * wob;
          d += `${i ? "L" : "M"}${(sd.x + Math.cos(a) * r).toFixed(1)} ${(
            sd.y + Math.sin(a) * r
          ).toFixed(1)}`;
        }
        d += "z";
      }
      return d ? `path("${d}")` : EMPTY_CLIP;
    };
  },

  /** Spreads from the top-left corner with irregular tendrils. */
  "Ink bleed": (w, h) => {
    const R = Math.hypot(w, h) / 0.55;
    return (p, t) => {
      const N = 60;
      let d = "M0 0";
      for (let i = 0; i <= N; i++) {
        const a = (i / N) * (Math.PI / 2);
        const wob =
          1 +
          0.24 * Math.sin(a * 7 + t * 1.2 + 1.7) +
          0.13 * Math.sin(a * 13 - t * 0.8) +
          0.08 * Math.sin(a * 23 + t * 2.0 + 4.2);
        const r = Math.max(p * R * wob, 0);
        d += `L${(Math.cos(a) * r).toFixed(1)} ${(Math.sin(a) * r).toFixed(1)}`;
      }
      return `path("${d}z")`;
    };
  },

  /** Fills from the bottom with a rolling surface plus floating droplets. */
  "Liquid rise": (w, h) => {
    const drops = Array.from({ length: 5 }, (_, j) => ({
      x: (0.1 + 0.8 * hash(j * 5 + 2)) * w,
      lead: 60 + hash(j * 5 + 3) * 140,
      r: 8 + hash(j * 5 + 4) * 14,
      ph: hash(j) * TAU,
    }));
    return (p, t) => {
      const A = 0.06 * h;
      const base = h + A + 20 - p * (h + 2 * A + 40);
      const N = 30;
      let d = `M0 ${h + 50}`;
      for (let i = 0; i <= N; i++) {
        const x = (w * i) / N;
        const y =
          base +
          Math.sin(x * 0.012 + t * 2.0) * 0.5 * A +
          Math.sin(x * 0.027 - t * 1.3 + 2.0) * 0.32 * A +
          Math.sin(x * 0.006 + t * 0.7 + 4.0) * 0.25 * A;
        d += `L${x.toFixed(1)} ${y.toFixed(1)}`;
      }
      d += `L${w} ${h + 50}z`;
      const fade = Math.sin(Math.min(p * 1.15, 1) * Math.PI);
      for (const dr of drops) {
        const r = dr.r * fade;
        if (r < 1) continue;
        const cy = base - dr.lead * (0.6 + 0.4 * Math.sin(t * 2 + dr.ph));
        for (let i = 0; i <= 14; i++) {
          const a = (i / 14) * TAU;
          const rr = r * (1 + 0.25 * Math.sin(a * 3 + t * 3 + dr.ph));
          d += `${i ? "L" : "M"}${(dr.x + Math.cos(a) * rr).toFixed(1)} ${(
            cy + Math.sin(a) * rr
          ).toFixed(1)}`;
        }
        d += "z";
      }
      return `path("${d}")`;
    };
  },
};

/** Swap this to change the reveal. Deliberately not user-facing. */
export const ACTIVE_EFFECT = "Blob burst";

/**
 * The rule whose clip-path we mutate. Created lazily on first use — never at
 * module scope, so importing this file is safe during SSR.
 */
let maskStyle: CSSStyleDeclaration | null = null;

function getMaskStyle(): CSSStyleDeclaration | null {
  if (maskStyle) return maskStyle;
  const styleEl = document.createElement("style");
  styleEl.textContent = "::view-transition-new(root){}";
  document.head.appendChild(styleEl);
  const rule = styleEl.sheet?.cssRules[0];
  // Narrow rather than cast: a stylesheet can be null and cssRules can be
  // blocked, and a broken mask should degrade to an instant switch.
  if (!(rule instanceof CSSStyleRule)) return null;
  maskStyle = rule.style;
  return maskStyle;
}

let busy = false;
/** Increments per run so a stale rAF loop can tell it has been superseded. */
let runId = 0;

/**
 * Animate a theme switch originating at `originEl`.
 *
 * @param applyTheme Performs the real theme change. Must leave the DOM in the
 *   new theme *synchronously* — the browser snapshots immediately after this
 *   returns. With next-themes the class is written in a passive effect, so
 *   callers wrap their `setTheme` in `flushSync`.
 * @param originEl The toggle that was clicked; its centre is the reveal origin.
 */
export async function animateThemeSwitch(
  applyTheme: () => void,
  originEl: HTMLElement | null,
): Promise<void> {
  // A second click while a reveal is running still has to change the theme.
  // Animating it would mean two loops writing the same singleton mask, so the
  // switch happens instantly instead — losing the change outright is the one
  // outcome that is never acceptable, and every other bail-out below applies
  // the theme too.
  if (busy) {
    applyTheme();
    return;
  }

  const prefersReduced = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  const mask = originEl && !prefersReduced ? getMaskStyle() : null;

  // Every bail-out runs the identical apply path, just without the wrapper —
  // an unsupported browser or a failed mask must never block the switch.
  if (!originEl || prefersReduced || !document.startViewTransition || !mask) {
    applyTheme();
    return;
  }

  busy = true;
  // Claimed before the try so `finally` can tell whether this run still owns
  // the mask. See the note on the rAF loop below.
  const generation = ++runId;
  try {
    const rect = originEl.getBoundingClientRect();
    const origin = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
    const frame = EFFECTS[ACTIVE_EFFECT](
      window.innerWidth,
      window.innerHeight,
      origin,
    );

    // Hide the new layer before the first frame, or it flashes fully painted.
    mask.clipPath = EMPTY_CLIP;

    const transition = document.startViewTransition(() => applyTheme());

    // The callback runs asynchronously, so a throw from applyTheme rejects
    // updateCallbackDone rather than this call — and the catch below, which
    // exists to absorb a *skipped* transition, would swallow it silently.
    // Report it separately; the theme genuinely failing to apply is a bug,
    // not the benign interruption that catch is for.
    transition.updateCallbackDone.catch((error: Error) => {
      console.error(error, "Theme failed to apply during the view transition");
    });

    await transition.ready;

    // A no-op keyframe on the pseudo holds the transition open for DURATION_MS;
    // without it the browser tears the layers down on the next frame.
    document.documentElement.animate([{ opacity: 1 }, { opacity: 1 }], {
      duration: DURATION_MS,
      pseudoElement: "::view-transition-new(root)",
    });

    // `transition.finished` can settle before the rAF loop reaches p === 1 —
    // a skipped or interrupted transition rejects immediately. That releases
    // `busy` while this loop is still scheduled, so a fast re-toggle could
    // start a second run and have both loops writing the same singleton
    // clipPath. Each run stops as soon as it is no longer the current one.
    const start = performance.now();
    const step = (now: number) => {
      if (generation !== runId) return;
      const p = ease(Math.min((now - start) / DURATION_MS, 1));
      mask.clipPath = frame(p, now / 1000);
      if (p < 1) requestAnimationFrame(step);
    };
    step(performance.now());

    await transition.finished;
  } catch {
    // A skipped or interrupted transition is not a failure: startViewTransition
    // has already run the callback, so the theme is applied either way.
  } finally {
    busy = false;
    // Only the newest run owns the mask; an older one bailing out here would
    // clear a reveal that is still in flight.
    if (generation === runId) mask.clipPath = "";
  }
}
